import { useEffect, useState } from "react";
import { CheckOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { TipSettings } from "../../../../../api/tipSettings";
import { useViewport } from "../../../../../hooks/useViewport";
import { formatCents } from "../../../../../utils/money";
import { GivenAmountField } from "./GivenAmountField";
import { useKeypadOpen } from "./keypadState";
import { MoneyKeypad } from "./MoneyKeypad";
import { tipAmountIssue } from "./tipMath";
import { useTipIssueText } from "./tipIssueText";
import type { TipEntryState } from "./useTipEntry";

// Колко след последния знак грешката за сумата („по-малка от сметката", „над
// максималния") се показва — иначе по време на писане („5" → „55") редът
// ѝ се появява и изчезва и модалът трепва.
const AMOUNT_ISSUE_DELAY_MS = 700;

/** Бакшишът (в центове), след като писането е спряло; `settleNow` — веднага
 *  (излизане от полето, Enter). „90" → „90.00" не е ново писане — сумата е същата. */
function useSettledTipCents(tipCents: number) {
  const [settledTipCents, setSettledTipCents] = useState(tipCents);
  useEffect(() => {
    if (settledTipCents === tipCents) return;
    const timer = setTimeout(() => setSettledTipCents(tipCents), AMOUNT_ISSUE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [tipCents, settledTipCents]);
  return { isTyping: settledTipCents !== tipCents, settleNow: () => setSettledTipCents(tipCents) };
}

/**
 * Отвореният бакшиш (дизайнът „Закрий"): „Сума дадена от клиента" с
 * „Премахни ×" (`GivenAmountField`) и под полето екранната клавиатура —
 * разпъната или свита, изборът се помни на компютъра (`keypadState.ts`).
 * Бакшишът е разликата спрямо поръчаното и се вижда до „Общо за плащане"
 * (CloseTipColumn); тук под полето са само проверките.
 */
export function TipEntryCard({
  tip,
  settings,
  disabled,
}: {
  tip: TipEntryState;
  settings: TipSettings;
  disabled: boolean;
}) {
  const { t } = useTranslation();
  const issueText = useTipIssueText();
  const keypad = useKeypadOpen();
  // Таблет с двете колони (модал 560) — по-дребните надписи се събират на един ред.
  const { isTablet, isNarrow } = useViewport();
  const tipCents = tip.tipCents;
  const hasTip = tipCents > 0;
  const typing = useSettledTipCents(tipCents);
  // Заключеният бутон и проверката при „Закриване" не чакат — само видимото.
  const amountIssue = typing.isTyping ? null : tipAmountIssue(tipCents, settings);
  // Методът на сметката е в настройките, но бакшишът с него не може да
  // влезе в бона — казва се, щом има бакшиш.
  const methodMessage = hasTip && tip.methodIssue ? issueText(tip.methodIssue) : null;
  const hasMessages =
    !!amountIssue || !!methodMessage || tip.needsConfirmation || !!tip.orderedChange;

  return (
    <div
      data-testid="tip-entry-section"
      className="flex flex-col"
      onClick={(event) => event.stopPropagation()}
    >
      <GivenAmountField
        label={t("closeTable.givenByClient")}
        value={tip.enteredText}
        placeholder={formatCents(tip.orderedCents)}
        onChange={tip.changeTotalWithTip}
        onNormalize={() => {
          tip.normalizeTotalWithTip();
          typing.settleNow();
        }}
        action={{ label: t("closeTable.tip.removeTip"), onClick: tip.removeTip }}
        keypad={keypad}
        hasError={!!amountIssue}
        isCompact={isTablet && !isNarrow}
        isPhone={isNarrow}
        disabled={disabled}
        leaveOnArrows
      />

      {/* На телефон екранна клавиатура няма — там е системната. */}
      {keypad.isOpen && !isNarrow && (
        <div className="mt-3">
          <MoneyKeypad onKey={tip.pressKeypadKey} disabled={disabled} />
        </div>
      )}

      {hasMessages && (
        <div className="mt-2.5 flex flex-col gap-2.5">
          {amountIssue && (
            <div role="alert" className="text-[12px] font-semibold leading-[1.45] text-pos-danger-ink">
              {amountIssue.code === "tip_negative"
                ? t("closeTable.tip.underBill", { amount: formatCents(tip.orderedCents) })
                : issueText(amountIssue)}
            </div>
          )}
          {methodMessage && (
            <div role="alert" className="text-[12px] font-semibold leading-[1.45] text-pos-danger-ink">
              {methodMessage}
            </div>
          )}

          {tip.needsConfirmation && tip.orderedChange === null && (
            // Палитрата на избраното в модала (избраният метод вдясно),
            // отметката — заоблена, като останалите в Маркиране.
            <label className="flex cursor-pointer items-start gap-2.5 rounded-[12px] border border-pos-accent/30 bg-pos-ready-bg px-3 py-2.5">
              <input
                type="checkbox"
                checked={tip.isConfirmed}
                disabled={disabled}
                onChange={(event) => tip.setConfirmed(event.target.checked)}
                className="peer sr-only"
              />
              <span
                aria-hidden
                className={`mt-px grid h-[18px] w-[18px] shrink-0 place-items-center rounded-[6px] border-[1.5px] text-white transition-colors peer-focus-visible:shadow-[0_0_0_3px_color-mix(in_srgb,var(--color-pos-accent)_30%,transparent)] ${
                  tip.isConfirmed ? "border-pos-accent bg-pos-accent" : "border-pos-check bg-pos-card"
                }`}
              >
                {tip.isConfirmed && <CheckOutlined className="text-[10px]" />}
              </span>
              <span className="min-w-0 text-[12.5px] leading-[1.45] text-pos-soft2">
                <span className="block font-semibold text-pos-accent-ink">
                  {issueText({
                    code: "tip_confirmation_required",
                    amount: formatCents(tipCents),
                    threshold: settings.warnAboveAmount ?? "",
                  })}
                </span>
                {t("closeTable.tip.confirm", { amount: formatCents(tipCents) })}
              </span>
            </label>
          )}

          {tip.orderedChange && (
            <div role="alert" className="rounded-[10px] border border-pos-danger-brd bg-pos-danger-bg px-3 py-2">
              <div className="text-[12px] font-semibold leading-[1.45] text-pos-danger-ink">
                {t("closeTable.tip.errors.bill_total_changed", {
                  old: formatCents(tip.orderedChange.fromCents),
                  new: formatCents(tip.orderedChange.toCents),
                })}
              </div>
              <button
                type="button"
                disabled={disabled}
                onClick={tip.acknowledgeOrderedChange}
                className="mt-2 h-8 cursor-pointer rounded-lg border border-pos-ctl bg-pos-card px-3 text-[12.5px] font-bold text-pos-ink transition-colors hover:border-pos-accent/40 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {t("closeTable.tip.reviewed")}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
